import { useState, useEffect } from 'react';

import { CheckCircleIcon } from '@chakra-ui/icons';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalCloseButton,
  Text,
  VStack,
  HStack,
  Box,
  Button,
  Link,
  Heading,
  Divider,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import Image from 'next/image';
import { QRCodeSVG } from 'qrcode.react';
import { isIOS, isAndroid, isMobile } from 'react-device-detect';

const APP_STORE_URL = 'https://apps.apple.com/us/app/equiyd/id6747093676';
const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.equiyd.app';

const DeprecationPopup = () => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Always show the popup on every page load
    setIsOpen(true);
  }, []);

  const handleClose = () => {
    setIsOpen(false);
  };

  const isDesktop = !isMobile;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size='lg'
      isCentered
      closeOnOverlayClick={true}
    >
      <ModalOverlay bg='blackAlpha.80' />
      <ModalContent mx={4} borderRadius='xl' overflow='hidden'>
        <ModalCloseButton zIndex={10} color='white' />
        <ModalHeader bg='purple.500' color='white' textAlign='center' py={8}>
          <VStack spacing={1}>
            <Text fontSize='subscript' fontWeight='normal' opacity={0.9}>
              Important Update
            </Text>
            <Heading fontSize='medium-title' fontWeight='bold'>
              Equiyd is Now a Mobile App
            </Heading>
          </VStack>
        </ModalHeader>

        <ModalBody py={6} px={8}>
          <VStack spacing={5} align='stretch'>
            <Text fontSize='paragraph' color='grey.700' textAlign='center'>
              This website is no longer being updated. Switch to our mobile app
              to access all the latest features and AI-powered tools.
            </Text>

            <Box
              bg='purple.50'
              p={4}
              borderRadius='lg'
              borderLeft='4px solid'
              borderColor='purple.500'
            >
              <Text
                fontSize='label'
                fontWeight='bold'
                color='purple.700'
                mb={3}
              >
                Exclusive AI Features
              </Text>
              <List spacing={2}>
                <ListItem fontSize='paragraph' color='purple.600'>
                  <ListIcon as={CheckCircleIcon} color='purple.500' />
                  <Text as='span' fontWeight='bold'>
                    Conformation Analysis
                  </Text>{' '}
                  — AI-powered horse assessment
                </ListItem>
                <ListItem fontSize='paragraph' color='purple.600'>
                  <ListIcon as={CheckCircleIcon} color='purple.500' />
                  <Text as='span' fontWeight='bold'>
                    Performance Assessments
                  </Text>{' '}
                  — Track and analyse your horse
                </ListItem>
                <ListItem fontSize='paragraph' color='purple.600'>
                  <ListIcon as={CheckCircleIcon} color='purple.500' />
                  <Text as='span' fontWeight='bold'>
                    Equi-Agent
                  </Text>{' '}
                  — Your AI horsecare adviser
                </ListItem>
              </List>
            </Box>

            <Box>
              <Text fontSize='label' fontWeight='bold' color='grey.700' mb={3}>
                Mobile-Only Features
              </Text>
              <List spacing={2}>
                <ListItem fontSize='paragraph' color='grey.600'>
                  <ListIcon as={CheckCircleIcon} color='success.500' />
                  <Text as='span' fontWeight='bold'>
                    Push Notifications
                  </Text>{' '}
                  — Never miss an update
                </ListItem>
                <ListItem fontSize='paragraph' color='grey.600'>
                  <ListIcon as={CheckCircleIcon} color='success.500' />
                  <Text as='span' fontWeight='bold'>
                    Real-time Reminders
                  </Text>{' '}
                  — Stay on top of your horse&apos;s care
                </ListItem>
              </List>
            </Box>

            <Box
              bg='error.50'
              p={4}
              borderRadius='lg'
              borderLeft='4px solid'
              borderColor='error.500'
            >
              <Text fontSize='subscript' color='error.700' fontWeight='normal'>
                This website will be discontinued. Download the app now to
                continue using Equiyd without interruption.
              </Text>
            </Box>

            {/* iOS Device - Show App Store only */}
            {isIOS && (
              <VStack spacing={4} pt={2}>
                <Link href={APP_STORE_URL} isExternal>
                  <Image
                    src='/images/appstore.png'
                    alt='Download on the App Store'
                    width={200}
                    height={60}
                    style={{ cursor: 'pointer' }}
                  />
                </Link>
              </VStack>
            )}

            {/* Android Device - Show Play Store only */}
            {isAndroid && (
              <VStack spacing={4} pt={2}>
                <Link href={PLAY_STORE_URL} isExternal>
                  <Image
                    src='/images/playstore.png'
                    alt='Get it on Google Play'
                    width={225}
                    height={60}
                    style={{ cursor: 'pointer' }}
                  />
                </Link>
              </VStack>
            )}

            {/* Desktop - Show both buttons and QR codes */}
            {isDesktop && (
              <VStack spacing={5} pt={2}>
                <Text
                  fontSize='subscript'
                  color='grey.500'
                  textAlign='center'
                  fontWeight='normal'
                >
                  Scan the QR code with your phone or click to download
                </Text>

                <HStack spacing={10} justify='center' wrap='wrap'>
                  {/* App Store */}
                  <VStack spacing={3}>
                    <Box
                      p={3}
                      bg='white'
                      borderRadius='lg'
                      border='1px solid'
                      borderColor='grey.200'
                    >
                      <QRCodeSVG value={APP_STORE_URL} size={100} level='M' />
                    </Box>
                    <Link href={APP_STORE_URL} isExternal>
                      <Image
                        src='/images/appstore.png'
                        alt='Download on the App Store'
                        width={135}
                        height={40}
                        style={{ cursor: 'pointer' }}
                      />
                    </Link>
                  </VStack>

                  {/* Play Store */}
                  <VStack spacing={3}>
                    <Box
                      p={3}
                      bg='white'
                      borderRadius='lg'
                      border='1px solid'
                      borderColor='grey.200'
                    >
                      <QRCodeSVG value={PLAY_STORE_URL} size={100} level='M' />
                    </Box>
                    <Link href={PLAY_STORE_URL} isExternal>
                      <Image
                        src='/images/playstore.png'
                        alt='Get it on Google Play'
                        width={152}
                        height={40}
                        style={{ cursor: 'pointer' }}
                      />
                    </Link>
                  </VStack>
                </HStack>
              </VStack>
            )}

            <Divider borderColor='grey.200' />

            <Button
              variant='ghost'
              size='sm'
              color='grey.500'
              fontWeight='normal'
              onClick={handleClose}
              _hover={{ color: 'grey.700', bg: 'grey.50' }}
            >
              Continue to web app (limited features)
            </Button>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default DeprecationPopup;
